import {
  type CamelizedType,
  formatData,
  getColumnsWithTransformedValues,
  transformObjectToCamelCase,
} from '@utilities';
import type {
  CamelizedTableDataPayloadColumn,
  TableDataPayload,
} from '@utilities/components/TableData/utilities/types';

type CamelizedPayloadItemWithTable<T extends PayloadItemWithTable> = CamelizedType<T> & {
  table?: {
    columns: CamelizedTableDataPayloadColumn[];
    data: Record<string, unknown>[];
  };
};

type CamelizedPayloadWithTable<T extends PayloadItemWithTable> = CamelizedType<{
  data: T[];
}> & {
  data: CamelizedPayloadItemWithTable<T>[];
};

interface PayloadItemWithTable {
  table?: TableDataPayload;
}

interface PayloadWithTable<T extends PayloadItemWithTable> {
  data: T[];
}

/**
 * camel-cases the payload and normalizes API table columns for TableData.
 */
export const camelizeData = <T extends PayloadItemWithTable>(data: PayloadWithTable<T>) => {
  const transformedData = transformObjectToCamelCase(data) as CamelizedPayloadWithTable<T>;
  return {
    ...transformedData,
    data: transformedData?.data?.map(({ table, ...item }) => ({
      ...item,
      ...(table && {
        table: {
          ...table,
          columns: camelizeTableColumns(table.columns),
        },
      }),
    })),
  };
};

/**
 * Normalizes subscribed prompt answer keys from API snake_case to frontend camelCase.
 */
export const normalizeSubscribedPromptAnswers = (answers: unknown): Record<string, unknown> => {
  if (!answers || typeof answers !== 'object' || Array.isArray(answers)) return {};

  const {
    subscribed_categories: subscribedCategories,
    subscribed_channel: subscribedChannels,
    ...restAnswers
  } = answers as Record<string, unknown>;

  return {
    ...restAnswers,
    ...(subscribedCategories ? { subscribedCategories } : {}),
    ...(subscribedChannels ? { subscribedChannels } : {}),
  };
};

/**
 * Normalizes subscribed prompt answer keys from frontend camelCase to API snake_case.
 */
export const denormalizeSubscribedPromptAnswers = (answers: unknown): Record<string, unknown> => {
  if (!answers || typeof answers !== 'object' || Array.isArray(answers)) return {};

  const { subscribedCategories, subscribedChannels, ...restAnswers } = answers as Record<
    string,
    unknown
  >;

  return {
    ...(subscribedCategories ? { subscribed_categories: subscribedCategories } : {}),
    ...(subscribedChannels ? { subscribed_channel: subscribedChannels } : {}),
    ...restAnswers,
  };
};

/**
 * updates columns with the fields the TableData components expects
 */
const camelizeTableColumns = (columns: CamelizedTableDataPayloadColumn[] = []) =>
  getColumnsWithTransformedValues(columns).map(({ footer, footerFormat, ...column }) => ({
    ...column,
    ...(footer !== null &&
      footer !== undefined && {
        Footer: footerFormat ? formatData({ format: footerFormat, value: footer }) : footer,
      }),
  }));
